import React from "react";
import {
  some,
  take,
  toNumber,
  capitalize,
  map,
  includes,
  toLower,
} from "lodash";
import { Table, TableProps } from "@/shared/widgets/Table";
import { AvatarWithName } from "@/shared/ui/Avatar";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { RoleSelectDropdown } from "../RoleSelectDropdown";
import { AccessLevelSelectDropdown } from "../AccessLevelSelectDropdown";
import { useTranslation } from "react-i18next";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ModalFileName } from "@/shared/modals";
import { UserOptionsDropdown } from "../UserOptionsDropdown";
import { AccessLevel, RoleType } from "@/shared/constants";
import { SubtitleText } from "@/shared/ui/Typography";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import { ResourceEnum } from "@/shared/constants/resources";
import {
  intersectOrFallback,
  usePermissionAdministration,
} from "@/shared/hooks/usePermissionAdministration";
import {
  getAccessLevelFilter,
  getIsAccessLevelEditable,
  getIsRoleEditable,
  getRoleFilter,
} from "@/shared/utils/selectors/getRolesAndAccess";
import { Center, Row } from "@/shared/ui";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";
import { cn } from "@/lib/utils";

interface MembersTableProps<T extends {}>
  extends Omit<TableProps<T>, "columns"> {
  data: T[];
  sort?: { key: string; direction: "asc" | "desc" };
  isFullList?: boolean;
  resource: {
    type: ResourceEnum;
    id: number;
  };
  readOnly?: boolean;
  disableRoleChange?: boolean;
  surveyConfig?: AssessmentSurveyType[];
  tooltips?: {
    [key: string]: string; // Key is the column key, value is the tooltip text.
  };
  onRoleChange?: (
    personId: number,
    role: RoleType
  ) => void | Promise<boolean | void>;
  onAccessLevelChange?: (
    personId: number,
    access_level: AccessLevel
  ) => void | Promise<boolean | void>;
  removePersonWithConfirm?: (params: {
    personId: number;
    name: string;
  }) => void;
}

function MembersTable<T extends { [key: string]: any }>(
  props: MembersTableProps<T>
) {
  const {
    data = [],
    surveyConfig,
    sort,
    isFullList = false,
    resource,
    readOnly = false,
    disableRoleChange = false,
    tooltips,
    onRoleChange,
    onAccessLevelChange,
    removePersonWithConfirm,
    ...rest
  } = props;

  const { t } = useTranslation();
  const [openMemberModal] = useModal(ModalFileName.MEMBER);

  const permissionAdmin = usePermissionAdministration({
    resourceType: resource.type,
    resourceId: resource.id,
  });

  const getRoleLabel = (role?: RoleType) => {
    if (!role) return undefined;
    switch (role) {
      case RoleType.owner:
        return t("common.owner");
      case RoleType.admin:
        return t("common.admin");
      case RoleType.member:
        return t("common.member");
      case RoleType.candidate:
        return t("common.candidate");
      case RoleType.contact:
        return t("common.contact");
      case RoleType.collaborator:
        return t("common.collaborator");
      default:
        return undefined;
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case "invited":
        return t("common.invited");
      case "contact":
        return t("common.contact");
      default:
        return null;
    }
  };

  const getDimensionInitial = (dimType: AssessmentSurveyType) => {
    switch (dimType) {
      case AssessmentSurveyType.IPIP_NEO_BIG_5:
      case AssessmentSurveyType.MAP:
        return "P"; // Personality
      case AssessmentSurveyType.MATCH_V_V2:
      case AssessmentSurveyType.SHWARTZ_VALUES:
        return "V"; // Values
      case AssessmentSurveyType.AP_COMPETENCIES:
      case AssessmentSurveyType.WISNIO_COMPETENCIES:
        return "C"; // Competencies
    }
  };

  const columns = {
    name: {
      title: t("people.name_column"),
      sortable: true,
      sortDirection: sort?.key === "name" ? sort.direction : undefined,
      accessor: (data: any) => (
        <AvatarWithName
          size="sm"
          direction="row"
          description={data?.email}
          avatar={data?.avatar}
          initials={data?.initials}
          isContact={data?.is_contact}
          name={data?.name}
        />
      ),
    },
    organizations: {
      title: t("people.organisations_column"),
      span: 2,
      hidden: !isFullList,
      className: "!whitespace-normal",
      accessor: (data: any) => {
        const max3Organizations = take(data.organizations, 3);
        const moreCount = (data.organizations?.length || 0) - 3;
        return (
          <SubtitleText>
            {max3Organizations?.map((org: any) => org.name).join(", ")}
            {moreCount > 0 && (
              <Tooltip>
                <TooltipTrigger>
                  <SubtitleText color="primary" className="ml-1">
                    {t("common.more_count", { count: moreCount })}
                  </SubtitleText>
                </TooltipTrigger>
                <TooltipContent className="max-w-[320px]">
                  {data.organizations?.map((org: any) => org.name).join(", ")}
                </TooltipContent>
              </Tooltip>
            )}
          </SubtitleText>
        );
      },
    },

    status: {
      title: t("common.status"),
      sortable: true,
      span: 1,
      hidden: !surveyConfig || import.meta.env.VITE_GPT_FLOW_CONFIG === "rraqa",
      accessor: (data: any) => {
        const label = getStatusLabel(data.status);
        if (!!label) {
          return (
            <StatusBadge
              label={getStatusLabel(data.status) || ""}
              variant={
                data.status === "complete"
                  ? "successFilled"
                  : data.status === "pending"
                  ? "warningFilled"
                  : data.status === "invited"
                  ? "primaryFilled"
                  : "neutralFilled"
              }
            />
          );
        }

        return (
          <Row className="gap-2">
            {map(surveyConfig, (dt) => {
              const isComplete = includes(data.completed_dim_types, dt);
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Center
                      className={cn(
                        "h-4 w-4 rounded-full items-center justify-center bg-gray-200 text-gray-500 !cursor-pointer",
                        isComplete && "bg-green-500 text-white"
                      )}
                    >
                      <span className="text-center leading-loose text-[10px]">
                        {getDimensionInitial(dt)}
                      </span>
                    </Center>
                  </TooltipTrigger>

                  <TooltipContent>
                    {`${t(`survey.${toLower(dt || "")}`)}: ${t(
                      isComplete ? "common.complete" : "common.incomplete"
                    )}`}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </Row>
        );
      },
    },
    role: {
      title: t("common.role"),
      sortable: true,
      className: "pl-4",
      span: 1,
      tooltipText: tooltips?.role,
      sortDirection: sort?.key === "role" ? sort.direction : undefined,
      accessor: (data: any) => {
        const canAdminChangeRole = permissionAdmin.canChangeRole;
        const canEdit =
          !readOnly &&
          !disableRoleChange &&
          !!onRoleChange &&
          canAdminChangeRole &&
          getIsRoleEditable(data.roleObject, resource.type);

        // Role editing stays disabled in general (`disableRoleChange`): supporting
        // arbitrary role changes would require de-escalation rules and conflict
        // handling for access levels. The single exception is promoting a CANDIDATE
        // to MEMBER, which is the required explicit step before a candidate can hold
        // any access level (TA-145). Promotion only — MEMBER is the sole option.
        const canPromoteCandidate =
          !readOnly &&
          !!onRoleChange &&
          canAdminChangeRole &&
          data.roleObject?.role === RoleType.candidate &&
          permissionAdmin.grantableRoles.includes(RoleType.member);

        if (!canEdit && !canPromoteCandidate) {
          return (
            <SubtitleText className="min-w-[140px] text-left pl-4">
              {getRoleLabel(data.role as RoleType) || capitalize(data.role)}
            </SubtitleText>
          );
        }

        return (
          <RoleSelectDropdown
            filter={
              canEdit
                ? (intersectOrFallback({
                    backendList: permissionAdmin.grantableRoles as any,
                    fallbackList: getRoleFilter(
                      data.roleObject,
                      resource.type
                    ) as any,
                  }) as RoleType[])
                : [RoleType.member]
            }
            value={data.role}
            onSelect={(role) => {
              return onRoleChange?.(data.id, role);
            }}
          />
        );
      },
    },
    access_level: {
      title: t("people.access_level_column"),
      sortable: true,
      className: "pl-4",
      span: 1,
      tooltipText: tooltips?.access_level,
      sortDirection: sort?.key === "access_level" ? sort.direction : undefined,
      accessor: (data: any) => {
        const accessIsEditable = getIsAccessLevelEditable(
          data.roleObject,
          resource.type
        );
        const canAdminChangeAccessLevel = permissionAdmin.canChangeAccessLevel;
        const canEdit =
          !readOnly && canAdminChangeAccessLevel && accessIsEditable;

        if (!canEdit) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <SubtitleText className="min-w-[140px] text-left pl-4">
                  {data.access_level
                    ? capitalize(data.access_level)
                    : t("common.access_level_none")}
                </SubtitleText>
              </TooltipTrigger>
              <TooltipContent side="left">
                {!accessIsEditable
                  ? t("membersTable.access_level_not_editable_for_role")
                  : !canAdminChangeAccessLevel
                  ? t("membersTable.no_permission_change_access_level")
                  : t("membersTable.no_permission_change_access_level")}
              </TooltipContent>
            </Tooltip>
          );
        }

        // Backend grantables are the source of truth. In particular, org FULL must only appear
        // when it is explicitly grantable.
        const canAssignFullAccessLevel =
          permissionAdmin.grantableAccessLevels.includes(AccessLevel.full);
        const fallbackFilter = getAccessLevelFilter(
          data.roleObject,
          resource.type,
          canAssignFullAccessLevel
        );

        return (
          <AccessLevelSelectDropdown
            filter={
              intersectOrFallback({
                backendList: permissionAdmin.grantableAccessLevels as any,
                fallbackList: fallbackFilter as any,
              }) as AccessLevel[]
            }
            // Backend may return null/undefined to represent "NONE" (e.g. candidate with no access).
            // Ensure the dropdown renders "No access" instead of the "Select" placeholder.
            value={(data.access_level ?? AccessLevel.none) as AccessLevel}
            onSelect={(access_level: AccessLevel) =>
              onAccessLevelChange?.(data.id, access_level)
            }
          />
        );
      },
    },

    actions: {
      title: "",
      span: 0,
      className: "text-right",
      accessor: (data: any) =>
        readOnly ? null : (
          <UserOptionsDropdown
            personId={data.id}
            isContact={data.is_contact}
            isPendingInvite={!!data.invite}
            inviteSecret={data.invite?.secret || ""}
            isProfileIncomplete={some(
              data.survey_completion,
              (survey) => !survey?.completed
            )}
            canRemove={permissionAdmin.canRevoke}
            onUserRemove={() => {
              if (!permissionAdmin.canRevoke) return;
              removePersonWithConfirm?.({
                personId: toNumber(data.id),
                name: data.name || "",
              });
            }}
          />
        ),
    },
  };

  const handleRowClick = (row: T) => {
    openMemberModal({
      member: row,
    });
  };

  return (
    <Table
      onRowClick={handleRowClick}
      {...rest}
      columns={columns}
      data={data}
    />
  );
}

export { MembersTable };
